import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Download, Printer, Search, MapPin, AlertTriangle, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import Sidebar from "./Sidebar";
import { useAuth } from "../context/AuthContext";
import {
  getOutOfOfficeReport,
  getOutOfOfficeSummary,
} from "../services/outOfOfficeReportService";
import { describeRecordLocation, LOCATION_STATUS } from "../utils/geolocation";
import { RETENTION_DAYS } from "./LocationPrivacyNotice";

const PAGE_SIZE = 100;

const STATUS_OPTIONS = [
  { value: LOCATION_STATUS.OUT_OFFICE, label: "Out of Office" },
  { value: LOCATION_STATUS.IN_OFFICE, label: "In Office" },
  { value: LOCATION_STATUS.UNAVAILABLE, label: "Location Unavailable" },
  { value: "all", label: "All statuses" },
];

const todayISO = () => new Date().toISOString().split("T")[0];
const daysAgoISO = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().split("T")[0];
};

const formatDateTime = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

export default function OutOfOfficeReport() {
  const navigate = useNavigate();
  const { token } = useAuth();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen((p) => !p);

  const [from, setFrom] = useState(daysAgoISO(30));
  const [to, setTo] = useState(todayISO());
  const [status, setStatus] = useState(LOCATION_STATUS.OUT_OFFICE);
  const [search, setSearch] = useState("");

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, limit: PAGE_SIZE, offset: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchReport = useCallback(
    async (offset = 0) => {
      if (!token) return;
      setLoading(true);
      setError("");
      try {
        const filters = { from, to, status, limit: PAGE_SIZE, offset };
        const [report, summaryRows] = await Promise.all([
          getOutOfOfficeReport(token, filters),
          getOutOfOfficeSummary(token, { from, to }),
        ]);
        setRows(report.rows);
        setPagination(report.pagination);
        setSummary(summaryRows);
      } catch (err) {
        const message =
          err.response?.status === 403
            ? "You do not have access to this report."
            : err.response?.data?.message || "Failed to load the report";
        setError(message);
        setRows([]);
        setSummary([]);
      } finally {
        setLoading(false);
      }
    },
    [token, from, to, status]
  );

  useEffect(() => {
    fetchReport(0);
  }, [fetchReport]);

  // Client-side search over the loaded page; the date/status filters are
  // applied server-side.
  const filteredRows = rows.filter((r) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (r.full_name || "").toLowerCase().includes(q) ||
      (r.emp_id || "").toLowerCase().includes(q) ||
      (r.employee_id || "").toLowerCase().includes(q) ||
      (r.department || "").toLowerCase().includes(q)
    );
  });

  const handleExportCSV = () => {
    if (filteredRows.length === 0) return toast.error("Nothing to export");

    const headers = [
      "Employee ID", "Employee", "Department", "Check In", "Check Out",
      "Status", "Location", "Distance (m)", "Nearest Office", "Latitude", "Longitude",
      "GPS Accuracy (m)", "Low Confidence",
    ];

    const rowsCsv = filteredRows.map((r) => {
      const location = describeRecordLocation(r, "check_in");
      const cell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      return [
        cell(r.employee_id || ""),
        cell(r.full_name || r.emp_id),
        cell(r.department || ""),
        cell(formatDateTime(r.check_in)),
        cell(formatDateTime(r.check_out)),
        cell(location?.label || ""),
        cell(r.check_in_address || location?.coordinates || ""),
        cell(r.distance_meters ?? ""),
        cell(r.nearest_office || ""),
        cell(r.check_in_latitude ?? ""),
        cell(r.check_in_longitude ?? ""),
        cell(r.check_in_accuracy ?? ""),
        cell(r.low_confidence ? "Yes" : "No"),
      ].join(",");
    });

    const csv = "data:text/csv;charset=utf-8," + [headers.join(","), ...rowsCsv].join("\n");
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csv));
    link.setAttribute("download", `Out_Of_Office_Report_${from}_to_${to}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const hasMore = pagination.offset + pagination.limit < pagination.total;

  return (
    <div className="page container">
      {sidebarOpen && <div className="overlay" onClick={toggleSidebar}></div>}
      <Sidebar sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <main className="main">
        <div className="hamburger" onClick={toggleSidebar}>☰</div>

        <div className="ooo-container">
          <header className="ooo-header">
            <div className="ooo-header-left">
              <button className="ooo-back-btn" onClick={() => navigate("/reports")}>
                <ChevronLeft size={20} />
              </button>
              <div>
                <h1>Out-of-Office Check-ins</h1>
                <p>Where employees checked in from, based on the location captured at check-in</p>
              </div>
            </div>
            <div className="ooo-header-actions">
              <button className="ooo-action-btn" onClick={() => fetchReport(0)} disabled={loading}>
                <RefreshCw size={15} className={loading ? "ooo-spin" : ""} /> Refresh
              </button>
              <button className="ooo-action-btn" onClick={handleExportCSV}>
                <Download size={15} /> Export CSV
              </button>
              <button className="ooo-action-btn" onClick={() => window.print()}>
                <Printer size={15} /> Print
              </button>
            </div>
          </header>

          {/* Filters */}
          <section className="ooo-filters">
            <label>
              <span>From</span>
              <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label>
              <span>To</span>
              <input type="date" value={to} min={from} max={todayISO()} onChange={(e) => setTo(e.target.value)} />
            </label>
            <label>
              <span>Status</span>
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <div className="ooo-search">
              <Search size={15} color="#94a3b8" />
              <input
                placeholder="Search name, ID or department…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </section>

          {error && <div className="ooo-error">{error}</div>}

          {/* Per-employee summary */}
          {summary.length > 0 && (
            <section className="ooo-summary">
              <h2>Summary for the period</h2>
              <div className="ooo-summary-grid">
                {summary.slice(0, 8).map((s) => (
                  <div className="ooo-summary-card" key={s.emp_id}>
                    <div className="ooo-summary-name">{s.full_name || s.emp_id}</div>
                    <div className="ooo-summary-dept">{s.department || "—"}</div>
                    <div className="ooo-summary-counts">
                      <span className="ooo-pill ooo-pill-in">{s.in_office_days} in office</span>
                      <span className="ooo-pill ooo-pill-out">{s.out_office_days} out</span>
                      {s.unavailable_days > 0 && (
                        <span className="ooo-pill ooo-pill-na">{s.unavailable_days} no location</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Detail table */}
          <section className="ooo-table-wrap">
            <table className="ooo-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Department</th>
                  <th>Check In</th>
                  <th>Check Out</th>
                  <th>Status</th>
                  <th>Location</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="ooo-empty">
                      {loading ? "Loading…" : "No records for these filters"}
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((r) => {
                    const location = describeRecordLocation(r, "check_in");
                    return (
                      <tr key={r.id}>
                        <td>
                          <div className="ooo-emp-name">{r.full_name || r.emp_id}</div>
                          <div className="ooo-emp-id">{r.employee_id || r.emp_id}</div>
                        </td>
                        <td>{r.department || "—"}</td>
                        <td className="ooo-num">{formatDateTime(r.check_in)}</td>
                        <td className="ooo-num">{formatDateTime(r.check_out)}</td>
                        <td>
                          {location && (
                            <span className={`ooo-badge ooo-badge-${location.tone}`} title={location.title}>
                              <MapPin size={12} />
                              {location.label}
                              {location.lowConfidence && <AlertTriangle size={11} />}
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="ooo-location">
                            {location?.detail || "—"}
                            {location?.mapUrl && (
                              <>
                                {" "}
                                <a href={location.mapUrl} target="_blank" rel="noopener noreferrer">
                                  map
                                </a>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </section>

          <div className="ooo-footer-row">
            <span>
              Showing {filteredRows.length} of {pagination.total} records
            </span>
            <div className="ooo-pager">
              <button
                disabled={pagination.offset === 0 || loading}
                onClick={() => fetchReport(Math.max(pagination.offset - PAGE_SIZE, 0))}
              >
                Previous
              </button>
              <button
                disabled={!hasMore || loading}
                onClick={() => fetchReport(pagination.offset + PAGE_SIZE)}
              >
                Next
              </button>
            </div>
          </div>

          <p className="ooo-note">
            <strong>Location Unavailable</strong> means the browser did not provide a position — most
            often because location permission was declined, which employees are entitled to do. It is
            not an attendance exception. Records marked with a warning icon had a wide GPS error
            radius and should not be treated as conclusive. Exact positions are retained for{" "}
            {RETENTION_DAYS} days.
          </p>
        </div>
      </main>

      <style>{`
        .ooo-container { padding: 24px; max-width: 1400px; margin: 0 auto; }
        .ooo-header {
          display: flex; justify-content: space-between; align-items: flex-start;
          gap: 16px; flex-wrap: wrap; margin-bottom: 20px;
        }
        .ooo-header-left { display: flex; align-items: flex-start; gap: 12px; }
        .ooo-header-left h1 { margin: 0; font-size: 22px; font-weight: 700; color: #0f172a; }
        .ooo-header-left p { margin: 4px 0 0; font-size: 13px; color: #64748b; }
        .ooo-back-btn {
          background: #fff; border: 1px solid #e2e8f0; border-radius: 8px;
          width: 36px; height: 36px; display: flex; align-items: center;
          justify-content: center; cursor: pointer; color: #475569; flex-shrink: 0;
        }
        .ooo-back-btn:hover { background: #f1f5f9; }
        .ooo-header-actions { display: flex; gap: 8px; flex-wrap: wrap; }
        .ooo-action-btn {
          display: inline-flex; align-items: center; gap: 6px;
          background: #fff; border: 1px solid #e2e8f0; border-radius: 50px;
          padding: 7px 16px; cursor: pointer; font-size: 13px;
          font-weight: 500; color: #334155;
        }
        .ooo-action-btn:hover { background: #f8fafc; }
        .ooo-action-btn:disabled { opacity: .6; cursor: not-allowed; }
        @keyframes ooo-spin { to { transform: rotate(360deg); } }
        .ooo-spin { animation: ooo-spin .8s linear infinite; }

        .ooo-filters {
          display: flex; gap: 14px; align-items: flex-end;
          flex-wrap: wrap; margin-bottom: 20px;
        }
        .ooo-filters label { display: flex; flex-direction: column; gap: 5px; font-size: 12px; color: #475569; font-weight: 500; }
        .ooo-filters input[type="date"], .ooo-filters select {
          padding: 7px 10px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 13px;
        }
        .ooo-search {
          display: flex; align-items: center; gap: 6px; background: #fff;
          border: 1px solid #e2e8f0; border-radius: 50px; padding: 6px 14px;
          flex: 1; min-width: 220px; max-width: 320px;
        }
        .ooo-search input { border: none; outline: none; font-size: 13px; width: 100%; }

        .ooo-error {
          background: #fef2f2; border: 1px solid #fecaca; color: #b91c1c;
          padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 16px;
        }

        .ooo-summary { margin-bottom: 22px; }
        .ooo-summary h2 { font-size: 15px; font-weight: 600; color: #334155; margin: 0 0 10px; }
        .ooo-summary-grid {
          display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 12px;
        }
        .ooo-summary-card {
          background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px;
        }
        .ooo-summary-name { font-size: 14px; font-weight: 600; color: #0f172a; }
        .ooo-summary-dept { font-size: 12px; color: #94a3b8; margin-bottom: 8px; }
        .ooo-summary-counts { display: flex; flex-wrap: wrap; gap: 6px; }
        .ooo-pill { font-size: 11px; font-weight: 600; padding: 3px 8px; border-radius: 999px; }
        .ooo-pill-in { background: #d1fae5; color: #047857; }
        .ooo-pill-out { background: #fef3c7; color: #b45309; }
        .ooo-pill-na { background: #e5e7eb; color: #6b7280; }

        .ooo-table-wrap {
          overflow-x: auto; background: #fff;
          border: 1px solid #e2e8f0; border-radius: 12px;
        }
        .ooo-table { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 900px; }
        .ooo-table th {
          text-align: left; padding: 11px 14px; font-size: 11px;
          text-transform: uppercase; letter-spacing: .04em; color: #64748b;
          border-bottom: 1px solid #e2e8f0; font-weight: 600;
        }
        .ooo-table td { padding: 12px 14px; border-bottom: 1px solid #f1f5f9; color: #1e293b; vertical-align: top; }
        .ooo-table tr:last-child td { border-bottom: none; }
        .ooo-num { font-variant-numeric: tabular-nums; white-space: nowrap; }
        .ooo-emp-name { font-weight: 600; }
        .ooo-emp-id { font-size: 11px; color: #94a3b8; }
        .ooo-empty { text-align: center; color: #94a3b8; padding: 30px 14px; }
        .ooo-location { font-size: 12px; color: #475569; max-width: 300px; line-height: 1.5; word-break: break-word; }
        .ooo-location a { color: #0ea5e9; text-decoration: none; }
        .ooo-location a:hover { text-decoration: underline; }

        .ooo-badge {
          display: inline-flex; align-items: center; gap: 4px;
          padding: 4px 9px; border-radius: 999px; font-size: 11.5px;
          font-weight: 600; white-space: nowrap;
        }
        .ooo-badge-in-office { background: #d1fae5; color: #047857; }
        .ooo-badge-out-office { background: #fef3c7; color: #b45309; }
        .ooo-badge-unavailable { background: #e5e7eb; color: #6b7280; }

        .ooo-footer-row {
          display: flex; justify-content: space-between; align-items: center;
          gap: 12px; margin-top: 14px; font-size: 12.5px; color: #64748b; flex-wrap: wrap;
        }
        .ooo-pager { display: flex; gap: 8px; }
        .ooo-pager button {
          padding: 6px 14px; border: 1px solid #cbd5e1; border-radius: 8px;
          background: #fff; font-size: 12.5px; cursor: pointer; color: #334155;
        }
        .ooo-pager button:disabled { opacity: .5; cursor: not-allowed; }

        .ooo-note {
          margin-top: 18px; padding: 12px 14px; background: #f8fafc;
          border-left: 3px solid #cbd5e1; border-radius: 0 8px 8px 0;
          font-size: 12.5px; color: #475569; line-height: 1.6;
        }
      `}</style>
    </div>
  );
}
